import { Controller } from "stimulus";
import createChannel from "cables/cable";
import marked from 'marked';
import $ from 'jquery';
import csrfPrefilter from "../components/csrf_prefilter";

const renderer = {
  heading(text, level) {
    return '#'.repeat(level) + text;
  },
  link(href, title, text) {
    return `<a href="${href}" title="${title || ''}" target="_blank">${text}</a>`;
  }
}
marked.setOptions({ breaks: true })
marked.use({ renderer })

export default class extends Controller {
  static targets = [
    "messages",
    "exampleMessage"
  ];

  initConversationMessageChannel() {
    this.conversationMessageChannel = createChannel(
      {
        channel: 'ConversationMessageChannel',
        conversation: this.conversationId
      }, {
        received: data => {
          if(this.messagesTarget.querySelectorAll(".conversation-message").length === 0) {
            this.exampleMessageTarget.remove();
          }
          this.appendMessage(
            data.id, data.content, data.read_at, data.created_at,
            data.sender.uid, data.sender.name, data.sender.image_url
          );
          // 自分のメッセージでなければ既読をつける
          if(data.sender.uid !== this.userUid) {
            csrfPrefilter();
            $.ajax({
              type: "post",
              url: `/conversations/${this.conversationId}/read_messages`,
              dataType: "json",
              data: { read_message_form: { last_message_id: data.id } }
            })
          }
        }
      }
    )
  }

  initConversationReadMessageChannel() {
    this.conversationReadMessageChannel = createChannel(
      {
        channel: 'ConversationReadMessageChannel',
        conversation: this.conversationId
      }, {
        received: data => {
          if(data.reader_uid !== this.userUid) {
            this.addReadMark(data.last_message_id);
          }
        }
      }
    )
  }

  addReadMark(lastMessageId) {
    this.messagesTarget.querySelectorAll(".conversation-message").forEach(el => {
      if(Number(el.dataset.messageId) <= Number(lastMessageId)) {
        el.classList.add("read");
      }
    });
  }

  appendMessage(id, content, readAt, createdAt, userUid, userName, userImageUrl) {
    let html = "";

    const [_, createdAtDate, createdAtTime] = createdAt.match(/(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/)
    const createdAtDateSlash = createdAtDate.replace(/-/g, "/");

    if(this.latestMessageDate !== createdAtDateSlash) {
      this.latestMessageDate = createdAtDateSlash;

      html += this.generateDateHtmlString(createdAtDateSlash);
    }

    html += this.generateMessageHtmlString(id, content, readAt, createdAtTime, userUid, userName, userImageUrl);

    this.messagesTarget.insertAdjacentHTML("beforeend", html);
  }

  scrollMessages(e) {
    if(window.scrollY !== 0) { return; }

    this.fetchPreviousMessage()
  }

  fetchPreviousMessage() {
    if(this.fetching || this.allFetched) { return; }
    this.fetching = true;

    fetch(`/conversations/${this.conversationId}/messages?oldest_message_id=${this.oldestMessageId}`)
      .then(RES => RES.json())
      .then(messages => {
        if(messages.length === 0) {
          this.allFetched = true;
          return;
        }

        const messagesHeight = this.messagesTarget.getBoundingClientRect().height;

        this.oldestMessageId = messages[0].id;
        messages.reverse().forEach(message => {
          this.prependMessage(
            message.id, message.content, message.read_at, message.created_at,
            message.user_uid, message.user_name, message.user_image_url
          );
        })
        this.fetching = false;
        window.scrollBy(0, this.messagesTarget.getBoundingClientRect().height - messagesHeight);
        if(window.scrollY === 0) {
          this.fetchPreviousMessage()
        }
      });
  }

  prependMessage(id, content, readAt, createdAt, userUid, userName, userImageUrl) {
    const [_, createdAtDate, createdAtTime] = createdAt.match(/(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/)
    const createdAtDateSlash = createdAtDate.replace(/-/g, "/");

    if(this.oldestMessageDate !== createdAtDateSlash) {
      this.oldestMessageDate = createdAtDateSlash;
      const dateHtml = this.generateDateHtmlString(createdAtDateSlash);
      this.messagesTarget.insertAdjacentHTML("afterbegin", dateHtml);
    }

    const messageHtml = this.generateMessageHtmlString(id, content, readAt, createdAtTime, userUid, userName, userImageUrl, createdAtTime);
    this.messagesTarget.children[0].insertAdjacentHTML("afterend", messageHtml);
  }

  generateDateHtmlString(date) {
    return `
      <div class="text-line-right">
        <p class="is-size-7">${date}</p>
      </div>
    `
  }

  generateMessageHtmlString(id, content, readAt, createdAtTime, userUid, userName, userImageUrl) {
    return `
      <div class="conversation-message py-3 px-2 ${readAt ? "read" : ""} ${userUid === this.userUid ? "my-message" : ""}" data-message-id="${id}">
        <article class="media is-flex is-align-items-center">
          <figure class="media-left">
            <p class="image is-32x32">
              <img class="has-background-white" src="${userImageUrl}">
            </p>
          </figure>
          <div class="media-content">
            <div class="is-flex is-align-items-center">
              <div class="is-flex is-align-items-center">
                <h3 class="title is-5 mr-2">${userName}</h3>
              </div>
              <p class="is-size-7 is-align-self-center">${createdAtTime}</p>
            </div>
          </div>
        </article>
        <div class="media-content read-mark-parent">
          <div class="content mt-3 mb-0">${marked(
            content
          )}</div>
          <div class="read-mark icon-text is-size-8">
            <span class="icon mr-0">
              <i class="fas fa-check"></i>
            </span>
            <span>既読</span>
          </div>
        </div>
      </div>
    `;
  }

  connect() {
    this.userUid = this.element.dataset.userUid;
    this.conversationId = this.element.dataset.conversationId;
    this.initConversationMessageChannel();
    this.initConversationReadMessageChannel();

    this.latestMessageDate = this.element.dataset.latestMessageDate;
    this.oldestMessageDate = this.element.dataset.oldestMessageDate;
    this.oldestMessageId = this.element.dataset.oldestMessageId;
    this.allFetched = false;

    setTimeout(() => {
      window.scrollBy(0, 9999);
      if(window.scrollY === 0) {
        this.fetchPreviousMessage()
      }
    }, 600)
  }

  disconnect() {
    this.conversationMessageChannel.unsubscribe();
    this.conversationReadMessageChannel.unsubscribe();
  }
}
